import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { executeFetchPlan } from "../services/cacheShareServices";
import { resolveFetchPlan } from "../taxonomy";
import { failureOf, PROVIDER_UNAVAILABLE } from "./useAllocationBoard";
import { filterTeams } from "./useCascadeFields";

export const TEAMS_FAILED = "Teams did not load for this selection. Try again.";

// What to tell the coach when the team list fails: the server's own
// sentence when the data provider is down and nothing is saved
// (provider_unavailable, "Football teams are unavailable right now, ..."),
// else the generic line.
export const teamsFailureText = (err) => {
  const failure = failureOf(err);
  return failure.code === PROVIDER_UNAVAILABLE && failure.detail ? failure.detail : TEAMS_FAILED;
};

// The status line under a team picker whose list failed: the server's
// sentence when it gave one (teamsErrorText other than the generic line),
// else `fallback`.
export const teamsStatusText = (teamsErrorText, fallback) =>
  teamsErrorText && teamsErrorText !== TEAMS_FAILED ? teamsErrorText : fallback;

export const EMPTY_FILTERS = {
  sport: null,
  gender: null,
  tier: null,
  region: null,
  body: null,
  subDivision: null,
  classDivision: null,
  conference: null,
  teamSearch: "",
};

// Cascade filters to team list (Coach Mode V2 CacheShare). A tier alone is
// enough to list teams (College unions every governing body), so the team
// search sits right after the tier and the governing body, league and
// conference only narrow the list. A changed scope clears the picked team.
// A list that fails to load sets `teamsError` (rather than reading as "no
// teams") and `retryTeams()` asks again; an answer for an older scope is
// dropped. `teamsErrorText` says why ('' while nothing failed).
// CacheShare scores the picked team, so the list asks only for teams it can
// score (no allTeams).
export const useTeamCascade = (initialFilters = EMPTY_FILTERS) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...initialFilters });
  const [teams, setTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamsError, setTeamsError] = useState(false);
  const [teamsErrorText, setTeamsErrorText] = useState("");
  const [attempt, setAttempt] = useState(0);
  const retryTeams = useCallback(() => setAttempt((n) => n + 1), []);

  const plan = useMemo(
    () =>
      resolveFetchPlan({
        sportValue: filters.sport?.value,
        gender: filters.gender?.value,
        tier: filters.tier?.value,
        region: filters.region?.value,
        body: filters.body,
        collegeUnion: true,
        subDivision: filters.subDivision,
        classDivision: filters.classDivision,
        conference: filters.conference?.value,
        teamSearch: filters.teamSearch,
      }),
    [filters],
  );
  // The plan is a fresh object each render: refetch only when it changes.
  const planKey = plan ? JSON.stringify(plan) : "";

  useEffect(() => {
    setSelectedTeam(null);
    setTeamsError(false);
    setTeamsErrorText("");
    if (!plan) {
      setTeams([]);
      setIsLoadingTeams(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoadingTeams(true);
    executeFetchPlan(plan, { throwOnError: true })
      .then((list) => {
        if (!cancelled) setTeams(list || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const text = teamsFailureText(err);
        toast.error(text);
        setTeams([]);
        setTeamsError(true);
        setTeamsErrorText(text);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTeams(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, attempt]);

  const filteredTeams = useMemo(() => filterTeams(teams, filters.teamSearch), [teams, filters.teamSearch]);
  const scopeName = filters.conference?.name || filters.subDivision?.name || filters.body?.name || filters.region?.name || filters.tier?.name || "";

  return { filters, setFilters, plan, teams, filteredTeams, isLoadingTeams, teamsError, teamsErrorText, retryTeams, selectedTeam, setSelectedTeam, scopeName };
};
